import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Collapse,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SchoolIcon from "@mui/icons-material/School";
import WarningIcon from "@mui/icons-material/Warning";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MinimizeIcon from "@mui/icons-material/Minimize";
import MaximizeIcon from "@mui/icons-material/Maximize";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

// Styled components
const StyledDrawer = styled(Drawer)(({ theme, isMinimized }) => ({
  "& .MuiDrawer-paper": {
    width: isMinimized ? 80 : 350,
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const PlayerItem = styled(ListItem)(({ theme }) => ({
  cursor: "pointer",
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  border: "1px solid transparent",
  transition: theme.transitions.create(["background-color"], {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const PlayerAvatar = styled(Avatar)(({ theme, classYear }) => ({
  backgroundColor:
    classYear === "Freshman"
      ? theme.palette.success.main
      : classYear === "Sophomore"
      ? theme.palette.info.main
      : classYear === "Junior"
      ? theme.palette.warning.main
      : classYear === "Senior"
      ? theme.palette.error.main
      : theme.palette.grey[500],
  width: 40,
  height: 40,
  fontSize: "0.9rem",
  fontWeight: "bold",
}));

const FilterSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 0),
  marginBottom: theme.spacing(1),
}));

const RosterDrawer = ({
  open,
  onClose,
  players = [],
  onAddPlayer,
  onRefresh,
  loading = false,
  depthChartPlayers = [],
  onAddToRoster = () => {},
  availablePositions = [], // Add available positions prop
  pagination = { currentPage: 1, totalPages: 1, total: 0, limit: 25 },
  onLoadMore = () => {},
  onPageChange = () => {},
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    position: "",
    classYear: "",
    eligibilityStatus: "",
    academicStatus: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [selectedPlayerForPosition, setSelectedPlayerForPosition] =
    useState(null);
  const [isMinimized, setIsMinimized] = useState(false);

  // Filter and sort players based on search and filters
  const filteredPlayers = players
    .filter((player) => {
      // Search filter
      const matchesSearch =
        searchQuery === "" ||
        player.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        player.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (player.jerseyNumber &&
          player.jerseyNumber.toString().includes(searchQuery)) ||
        (player.rosterPosition &&
          player.rosterPosition
            .toLowerCase()
            .includes(searchQuery.toLowerCase()));

      // Position filter
      const matchesPosition =
        !filters.position || player.rosterPosition === filters.position;

      // Class year filter
      const matchesClassYear =
        !filters.classYear || player.classYear === filters.classYear;

      // Eligibility filter
      const matchesEligibility =
        !filters.eligibilityStatus ||
        player.eligibilityStatus === filters.eligibilityStatus;

      // Academic status filter
      const matchesAcademic =
        !filters.academicStatus ||
        player.academicStatus === filters.academicStatus;

      return (
        matchesSearch &&
        matchesPosition &&
        matchesClassYear &&
        matchesEligibility &&
        matchesAcademic
      );
    })
    .sort((a, b) => {
      // Sort by current roster status first (current roster players at top)
      if (a.isCurrentRoster && !b.isCurrentRoster) return -1;
      if (!a.isCurrentRoster && b.isCurrentRoster) return 1;

      // Then sort by last name alphabetically within each group
      const aLastName = a.lastName || "";
      const bLastName = b.lastName || "";
      return aLastName.localeCompare(bLastName);
    });

  // Get unique values for filters
  const uniquePositions = [
    ...new Set(players.map((player) => player.rosterPosition).filter(Boolean)),
  ].sort();
  const uniqueClassYears = [
    ...new Set(players.map((player) => player.classYear).filter(Boolean)),
  ].sort();
  const uniqueEligibilityStatuses = [
    ...new Set(
      players.map((player) => player.eligibilityStatus).filter(Boolean)
    ),
  ].sort();
  const uniqueAcademicStatuses = [
    ...new Set(players.map((player) => player.academicStatus).filter(Boolean)),
  ].sort();

  // Handle search change
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle filter change
  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      position: "",
      classYear: "",
      eligibilityStatus: "",
      academicStatus: "",
    });
    setSearchQuery("");
  };

  // Handle adding player to current roster
  const handleAddToCurrentRoster = async (player) => {
    try {
      await onAddToRoster(player);
      // Refresh the player list
      onRefresh();
    } catch (error) {
      console.error("Error adding player to roster:", error);
    }
  };

  // Handle opening position selection dialog
  const handleOpenPositionDialog = (player) => {
    setSelectedPlayerForPosition(player);
    setPositionDialogOpen(true);
  };

  // Handle closing position selection dialog
  const handleClosePositionDialog = () => {
    setPositionDialogOpen(false);
    setSelectedPlayerForPosition(null);
  };

  // Handle assigning player to position
  const handleAssignToPosition = (positionId, depth = 1) => {
    if (selectedPlayerForPosition && onAddPlayer) {
      onAddPlayer(selectedPlayerForPosition, positionId, depth);
      handleClosePositionDialog();
    }
  };

  // Check if player is in depth chart
  const isPlayerInDepthChart = (playerId) => {
    return depthChartPlayers.some(
      (dcPlayer) =>
        dcPlayer.playerId._id === playerId || dcPlayer.playerId === playerId
    );
  };

  // Get player initials for avatar
  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return "?";
    const first = firstName ? firstName.charAt(0) : "";
    const last = lastName ? lastName.charAt(0) : "";
    return first + last || "?";
  };

  // Get status icons for player
  const getPlayerStatusIcons = (player) => {
    const icons = [];

    if (player.injuryStatus?.isInjured) {
      icons.push(
        <Tooltip
          key="injury"
          title={`Injured: ${
            player.injuryStatus.injuryType || "Details not available"
          }`}
        >
          <LocalHospitalIcon fontSize="small" color="error" />
        </Tooltip>
      );
    }

    if (player.transferStatus && player.transferStatus !== "none") {
      icons.push(
        <Tooltip key="transfer" title={`Transfer: ${player.transferStatus}`}>
          <SchoolIcon fontSize="small" color="info" />
        </Tooltip>
      );
    }

    if (
      player.academicStatus === "concern" ||
      player.academicStatus === "probation"
    ) {
      icons.push(
        <Tooltip
          key="academic"
          title={`Academic Status: ${player.academicStatus}`}
        >
          <WarningIcon fontSize="small" color="warning" />
        </Tooltip>
      );
    }

    return icons;
  };

  return (
    <StyledDrawer
      anchor="left"
      open={open}
      onClose={onClose}
      variant="temporary"
      isMinimized={isMinimized}
      ModalProps={{
        keepMounted: true, // Better mobile performance
        disableEnforceFocus: true, // Prevent focus enforcement issues
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          {isMinimized ? "Players" : "Player Database"}
        </Typography>
        <Box>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          {!isMinimized && (
            <Tooltip title="Toggle Filters">
              <IconButton
                size="small"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FilterListIcon color={showFilters ? "primary" : "default"} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isMinimized ? "Expand" : "Minimize"}>
            <IconButton
              size="small"
              onClick={() => setIsMinimized(!isMinimized)}
            >
              {isMinimized ? <MaximizeIcon /> : <MinimizeIcon />}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {!isMinimized && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click assign to add players to depth chart positions
          </Typography>

          {/* Search */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search players..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            size="small"
            sx={{ mb: 2 }}
          />

          {/* Filters */}
          <Collapse in={showFilters}>
            <FilterSection>
              <Box
                sx={{ display: "grid", gap: 1, gridTemplateColumns: "1fr 1fr" }}
              >
                <FormControl size="small">
                  <InputLabel>Position</InputLabel>
                  <Select
                    value={filters.position}
                    onChange={(e) =>
                      handleFilterChange("position", e.target.value)
                    }
                    label="Position"
                  >
                    <MenuItem value="">All</MenuItem>
                    {uniquePositions.map((position) => (
                      <MenuItem key={position} value={position}>
                        {position}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <InputLabel>Class</InputLabel>
                  <Select
                    value={filters.classYear}
                    onChange={(e) =>
                      handleFilterChange("classYear", e.target.value)
                    }
                    label="Class"
                  >
                    <MenuItem value="">All</MenuItem>
                    {uniqueClassYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <InputLabel>Eligibility</InputLabel>
                  <Select
                    value={filters.eligibilityStatus}
                    onChange={(e) =>
                      handleFilterChange("eligibilityStatus", e.target.value)
                    }
                    label="Eligibility"
                  >
                    <MenuItem value="">All</MenuItem>
                    {uniqueEligibilityStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small">
                  <InputLabel>Academic</InputLabel>
                  <Select
                    value={filters.academicStatus}
                    onChange={(e) =>
                      handleFilterChange("academicStatus", e.target.value)
                    }
                    label="Academic"
                  >
                    <MenuItem value="">All</MenuItem>
                    {uniqueAcademicStatuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Button size="small" onClick={clearFilters} color="error">
                  Clear Filters
                </Button>
                <IconButton size="small" onClick={() => setShowFilters(false)}>
                  <ExpandLessIcon />
                </IconButton>
              </Box>
            </FilterSection>
            <Divider sx={{ mb: 2 }} />
          </Collapse>

          {/* Player Count and Pagination Info */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {filteredPlayers.length} of {pagination.total} players
              {pagination.totalPages > 1 && (
                <span>
                  {" "}
                  • Page {pagination.currentPage} of {pagination.totalPages}
                </span>
              )}
            </Typography>
            {Object.values(filters).some((v) => v !== "") && (
              <Chip
                label="Filtered"
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>

          {/* Players List */}
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredPlayers.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {players.length === 0
                  ? "No prospects found in database."
                  : "No players match your search criteria."}
              </Alert>
            ) : (
              <List disablePadding>
                {filteredPlayers.map((player, index) => {
                  const statusIcons = getPlayerStatusIcons(player);
                  const isFirstPlayer = index === 0;
                  const isFirstProspect =
                    index > 0 &&
                    !player.isCurrentRoster &&
                    filteredPlayers[index - 1].isCurrentRoster;
                  const hasCurrentRosterPlayers = filteredPlayers.some(
                    (p) => p.isCurrentRoster
                  );

                  return (
                    <React.Fragment key={player._id}>
                      {/* Current Roster header for first player if they're on roster */}
                      {isFirstPlayer &&
                        player.isCurrentRoster &&
                        hasCurrentRosterPlayers && (
                          <Box sx={{ mb: 2 }}>
                            <Divider>
                              <Chip
                                label="Current Roster"
                                size="small"
                                variant="outlined"
                                color="primary"
                              />
                            </Divider>
                          </Box>
                        )}

                      {/* Section divider between current roster and prospects */}
                      {isFirstProspect && (
                        <Box sx={{ my: 2 }}>
                          <Divider>
                            <Chip
                              label="Prospects"
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          </Divider>
                        </Box>
                      )}

                      <PlayerItem
                        sx={{
                          mb: 1,
                        }}
                      >
                        <ListItemAvatar>
                          <PlayerAvatar classYear={player.classYear}>
                            {getInitials(player.firstName, player.lastName)}
                          </PlayerAvatar>
                        </ListItemAvatar>

                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              <Typography variant="body2" fontWeight="medium">
                                {player.firstName} {player.lastName}
                              </Typography>
                              {statusIcons.length > 0 && (
                                <Box sx={{ display: "flex", gap: 0.25 }}>
                                  {statusIcons}
                                </Box>
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                #{player.jerseyNumber || "??"} •{" "}
                                {player.rosterPosition || "No position"} •{" "}
                                {player.classYear || "N/A"}
                              </Typography>
                              {player.height && player.weight && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                >
                                  {player.height}, {player.weight} lbs
                                </Typography>
                              )}
                            </Box>
                          }
                        />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {player.isCurrentRoster ? (
                            // Player is on current roster - show assign option
                            <Tooltip title="Assign to position">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPositionDialog(player)}
                                color="primary"
                              >
                                <AssignmentIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            // Player is not on roster - show add to roster button
                            <Tooltip title="Add to current roster">
                              <IconButton
                                size="small"
                                onClick={() => handleAddToCurrentRoster(player)}
                                color="primary"
                              >
                                <PersonAddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}

                          {isPlayerInDepthChart(player._id) && (
                            <Tooltip title="Already in depth chart">
                              <Chip
                                label="In Chart"
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </PlayerItem>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mt: 2,
                mb: 1,
                gap: 2,
              }}
            >
              <IconButton
                size="small"
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage <= 1 || loading}
              >
                <NavigateBeforeIcon />
              </IconButton>

              <Typography variant="caption" color="text.secondary">
                {pagination.currentPage} / {pagination.totalPages}
              </Typography>

              <IconButton
                size="small"
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={
                  pagination.currentPage >= pagination.totalPages || loading
                }
              >
                <NavigateNextIcon />
              </IconButton>
            </Box>
          )}

          {/* Footer */}
          <Divider sx={{ mt: 2, mb: 1 }} />
          <Typography variant="caption" color="text.secondary" align="center">
            Add players to current roster, then click assign to add to depth
            chart positions
          </Typography>
        </>
      )}

      {/* Minimized view - show just a few key players */}
      {isMinimized && (
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {filteredPlayers.filter((p) => p.isCurrentRoster).length} on roster
          </Typography>
          <List disablePadding>
            {filteredPlayers
              .filter((p) => p.isCurrentRoster)
              .slice(0, 5)
              .map((player) => (
                <ListItem
                  key={player._id}
                  sx={{
                    cursor: "pointer",
                    mb: 0.5,
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={() => handleOpenPositionDialog(player)}
                >
                  <ListItemAvatar>
                    <PlayerAvatar classYear={player.classYear}>
                      {getInitials(player.firstName, player.lastName)}
                    </PlayerAvatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="caption" noWrap>
                        {player.firstName} {player.lastName}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {player.rosterPosition}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
          </List>
          {filteredPlayers.filter((p) => p.isCurrentRoster).length > 5 && (
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ mt: 1 }}
            >
              +{filteredPlayers.filter((p) => p.isCurrentRoster).length - 5}{" "}
              more
            </Typography>
          )}
        </Box>
      )}

      {/* Position Selection Dialog */}
      <Dialog
        open={positionDialogOpen}
        onClose={handleClosePositionDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign {selectedPlayerForPosition?.firstName}{" "}
          {selectedPlayerForPosition?.lastName} to Position
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a position and depth level for this player:
          </Typography>

          {availablePositions.length > 0 ? (
            <List>
              {availablePositions.map((position) => (
                <Box key={position.positionId} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {position.positionName} ({position.abbreviation}) -{" "}
                    {position.group}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, ml: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleAssignToPosition(position.positionId, 1)
                      }
                    >
                      1st String (Starter)
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleAssignToPosition(position.positionId, 2)
                      }
                    >
                      2nd String
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleAssignToPosition(position.positionId, 3)
                      }
                    >
                      3rd String
                    </Button>
                  </Box>
                </Box>
              ))}
            </List>
          ) : (
            <Alert severity="info">
              No positions available. Please create positions in the depth chart
              first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePositionDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </StyledDrawer>
  );
};

export default RosterDrawer;
